import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import Popover from 'tio-ui/components/popover';
import type { DrilldownOption } from '../../utils/dashboard-builder/drilldown.ts';

// A 1px fixed anchor stands in for the trigger: canvas bars have no DOM to register.

interface DrilldownMenuSignature {
  Args: {
    /** Pointer position in viewport px; the menu is closed while unset. */
    anchor?: { x: number; y: number };
    /** The dimension choices on offer, one row each; an empty list keeps the menu closed. */
    options: DrilldownOption[];
    /** Fires with the chosen option; the caller applies its drilldowns and clears `anchor`. */
    onPick: (option: DrilldownOption) => void;
    /** Fires on Escape or an outside click; the caller clears `anchor`. */
    onClose: () => void;
  };
}

export default class DrilldownMenu extends Component<DrilldownMenuSignature> {
  get isOpen() {
    return Boolean(this.args.anchor) && this.args.options.length > 0;
  }

  anchorStyle = (anchor: { x: number; y: number }) =>
    htmlSafe(
      `position:fixed;left:${anchor.x}px;top:${anchor.y}px;width:1px;height:1px;pointer-events:none;`
    );

  /** The popover's deferred close may fire after a second click opened a new menu; only the current anchor closes. */
  closeFor = (anchor: { x: number; y: number }) => {
    if (anchor === this.args.anchor) this.args.onClose();
  };

  <template>
    {{#each (array @anchor) key="@identity" as |anchor|}}
      {{#if anchor}}
        <Popover
          @isOpen={{this.isOpen}}
          @onClose={{fn this.closeFor anchor}}
          @placement="bottom-start"
          @maxWidth={{320}}
          @ariaLabel="Filter the dashboard by"
        >
          <:trigger as |t|>
            <span style={{this.anchorStyle anchor}} aria-hidden="true" {{t.register}}></span>
          </:trigger>
          <:body>
            <div class="tio-col-flush py-1" role="menu" data-test-drilldown-menu>
              <div
                class="px-3 pb-1 tio-text-micro tio-type-weight-semibold uppercase tracking-wide tio-fg-muted"
              >
                Filter dashboard by
              </div>
              {{#each @options as |option|}}
                <button
                  type="button"
                  role="menuitem"
                  class="app-popover-option text-left"
                  data-test-drilldown-option
                  {{on "click" (fn @onPick option)}}
                >{{option.label}}</button>
              {{/each}}
            </div>
          </:body>
        </Popover>
      {{/if}}
    {{/each}}
  </template>
}
